import React, { useState, useEffect } from 'react';
import {
    Card,
    Typography,
    Button,
    Input,
    Select,
    Option,
    Chip,
    Alert,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from "@material-tailwind/react";
import {
    FaFileAlt,
    FaDownload,
    FaSearch,
    FaFilter,
    FaSyncAlt,
    FaChevronLeft,
    FaChevronRight,
    FaExclamationTriangle,
    FaInfoCircle,
    FaBug,
    FaTimes
} from 'react-icons/fa';

const LogBrowser = ({ rootUrl, token }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [pageSize, setPageSize] = useState(50);

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        level: '',
        startDate: '',
        endDate: '',
        source: ''
    });

    // Available log levels and sources
    const [logLevels, setLogLevels] = useState([]);
    const [logSources, setLogSources] = useState([]);

    // UI State
    const [showFilters, setShowFilters] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showLogDetail, setShowLogDetail] = useState(false);

    // Fetch logs from API
    const fetchLogs = async (page = 1, resetFilters = false) => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                page: page.toString(),
                limit: pageSize.toString(),
                ...(resetFilters ? {} : filters)
            });

            const response = await fetch(`${rootUrl}/logs?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                // Try to parse error message from response
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
                } catch (parseError) {
                    // If JSON parsing fails, use status text
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
            }

            const data = await response.json();
            if (data.success) {
                setLogs(data.logs);
                setCurrentPage(data.pagination.currentPage);
                setTotalPages(data.pagination.totalPages);
                setTotalLogs(data.pagination.totalRecords);
                setLogLevels(data.meta.levels || []);
                setLogSources(data.meta.sources || []);
            } else {
                throw new Error(data.error || data.message || 'Failed to fetch logs');
            }
        } catch (err) {
            setError(err.message);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    // Export logs
    const exportLogs = async (format = 'csv') => {
        try {
            const params = new URLSearchParams({
                format,
                ...filters
            });

            const response = await fetch(`${rootUrl}/logs/export?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                // Try to parse error message from response
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || errorData.message || 'Export failed');
                } catch (parseError) {
                    // If JSON parsing fails, use generic message
                    throw new Error('Export failed');
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `logs-${new Date().toISOString().split('T')[0]}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setError(`Export failed: ${err.message}`);
        }
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Apply filters
    const applyFilters = () => {
        setCurrentPage(1);
        fetchLogs(1);
        setShowFilters(false);
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({
            search: '',
            level: '',
            startDate: '',
            endDate: '',
            source: ''
        });
        setCurrentPage(1);
        fetchLogs(1, true);
        setShowFilters(false);
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    // Get log level color
    const getLogLevelColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'error': return 'red';
            case 'warn': case 'warning': return 'amber';
            case 'info': return 'blue';
            case 'debug': return 'green';
            default: return 'gray';
        }
    };

    // Get log level icon
    const getLogLevelIcon = (level) => {
        switch (level?.toLowerCase()) {
            case 'error': return <FaExclamationTriangle />;
            case 'warn': case 'warning': return <FaExclamationTriangle />;
            case 'info': return <FaInfoCircle />;
            case 'debug': return <FaBug />;
            default: return <FaFileAlt />;
        }
    };

    // Pagination handlers
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            fetchLogs(page);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="log-browser">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">System Logs</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => setShowFilters(!showFilters)}
                        variant="outlined"
                        size="sm"
                        className="flex items-center gap-2"
                    >
                        <FaFilter /> Filters
                    </Button>
                    <Button
                        onClick={() => fetchLogs(currentPage)}
                        className="bg-orange-500 hover:bg-orange-600"
                        size="sm"
                    >
                        <FaSyncAlt className="mr-2" /> Refresh
                    </Button>
                    <Button
                        onClick={() => exportLogs('csv')}
                        variant="outlined"
                        size="sm"
                        className="flex items-center gap-2"
                    >
                        <FaDownload /> Export CSV
                    </Button>

                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <Card className="p-4 mb-6">
                    <Typography variant="h6" className="mb-4">Filters</Typography>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                        <Input
                            label="Search in message"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            icon={<FaSearch />}
                        />
                        <Select
                            label="Log Level"
                            value={filters.level}
                            onChange={(value) => handleFilterChange('level', value)}
                        >
                            <Option value="">All Levels</Option>
                            {logLevels.map(level => (
                                <Option key={level} value={level}>{level.toUpperCase()}</Option>
                            ))}
                        </Select>
                        <Select
                            label="Source"
                            value={filters.source}
                            onChange={(value) => handleFilterChange('source', value)}
                        >
                            <Option value="">All Sources</Option>
                            {logSources.map(source => (
                                <Option key={source} value={source}>{source}</Option>
                            ))}
                        </Select>
                        <Input
                            type="datetime-local"
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                        <Input
                            type="datetime-local"
                            label="End Date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={applyFilters} size="sm" className="bg-blue-500">
                            Apply Filters
                        </Button>
                        <Button onClick={clearFilters} variant="outlined" size="sm">
                            Clear All
                        </Button>
                    </div>
                </Card>
            )}

            {error && (
                <Alert color="red" className="mb-4">
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && logs.length === 0 && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
                </div>
            )}

            {/* Stats */}
            {!loading && (
                <div className="flex justify-between items-center mb-4">
                    <Typography variant="small" color="blue-gray">
                        Showing {logs.length} of {totalLogs} logs
                    </Typography>
                    <div className="flex items-center gap-2">
                        <Typography variant="small" color="blue-gray">Logs per page:</Typography>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(parseInt(e.target.value))}
                            className="w-16 h-8 px-2 text-sm border border-blue-gray-200 rounded focus:outline-none focus:border-blue-500"
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </select>
                    </div>
                </div>
            )}

            {/* Logs Table */}
            {!loading && (
                <Card className="w-full overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-max table-auto text-left">
                            <thead>
                                <tr className="border-b border-blue-gray-100 bg-blue-gray-50/50">
                                    <th className="p-4 w-20">Level</th>
                                    <th className="p-4 w-32">Timestamp</th>
                                    <th className="p-4 w-24">Source</th>
                                    <th className="p-4">Message</th>
                                    <th className="p-4 w-20">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center">
                                            <Typography color="blue-gray" className="font-normal">
                                                No logs found
                                            </Typography>
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log, index) => (
                                        <tr key={index} className="border-b border-blue-gray-50 hover:bg-blue-gray-50/50">
                                            <td className="p-4">
                                                <Chip
                                                    variant="ghost"
                                                    size="sm"
                                                    value={log.level}
                                                    color={getLogLevelColor(log.level)}
                                                    icon={getLogLevelIcon(log.level)}
                                                />
                                            </td>
                                            <td className="p-4">
                                                <Typography variant="small" color="blue-gray" className="font-mono">
                                                    {formatTimestamp(log.timestamp)}
                                                </Typography>
                                            </td>
                                            <td className="p-4">
                                                <Typography variant="small" color="blue-gray">
                                                    {log.source || 'system'}
                                                </Typography>
                                            </td>
                                            <td className="p-4 max-w-md">
                                                <Typography variant="small" color="blue-gray" className="truncate">
                                                    {log.message}
                                                </Typography>
                                            </td>
                                            <td className="p-4">
                                                <Button
                                                    size="sm"
                                                    variant="outlined"
                                                    color="blue"
                                                    onClick={() => {
                                                        setSelectedLog(log);
                                                        setShowLogDetail(true);
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div className="flex justify-center items-center mt-6 gap-4">
                    <Button
                        variant="outlined"
                        size="sm"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <FaChevronLeft />
                    </Button>

                    <div className="flex items-center gap-2">
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            return (
                                <Button
                                    key={pageNum}
                                    variant={currentPage === pageNum ? "filled" : "outlined"}
                                    size="sm"
                                    onClick={() => goToPage(pageNum)}
                                    className={currentPage === pageNum ? "bg-orange-500" : ""}
                                >
                                    {pageNum}
                                </Button>
                            );
                        })}
                    </div>

                    <Button
                        variant="outlined"
                        size="sm"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        <FaChevronRight />
                    </Button>

                    <Typography variant="small" color="blue-gray">
                        Page {currentPage} of {totalPages}
                    </Typography>
                </div>
            )}

            {/* Log Detail Modal */}
            <Dialog open={showLogDetail} handler={() => setShowLogDetail(false)} size="lg">
                <DialogHeader className="flex justify-between items-center">
                    <Typography variant="h5">Log Details</Typography>
                    <Button
                        variant="text"
                        onClick={() => setShowLogDetail(false)}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <FaTimes />
                    </Button>
                </DialogHeader>
                <DialogBody className="max-h-96 overflow-y-auto">
                    {selectedLog && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="small" className="font-semibold mb-1">Level:</Typography>
                                    <Chip
                                        variant="ghost"
                                        size="sm"
                                        value={selectedLog.level}
                                        color={getLogLevelColor(selectedLog.level)}
                                        icon={getLogLevelIcon(selectedLog.level)}
                                    />
                                </div>
                                <div>
                                    <Typography variant="small" className="font-semibold mb-1">Source:</Typography>
                                    <Typography variant="small">{selectedLog.source || 'system'}</Typography>
                                </div>
                            </div>

                            <div>
                                <Typography variant="small" className="font-semibold mb-1">Timestamp:</Typography>
                                <Typography variant="small" className="font-mono">
                                    {formatTimestamp(selectedLog.timestamp)}
                                </Typography>
                            </div>

                            <div>
                                <Typography variant="small" className="font-semibold mb-1">Message:</Typography>
                                <div className="bg-gray-100 p-3 rounded font-mono text-sm whitespace-pre-wrap">
                                    {selectedLog.message}
                                </div>
                            </div>

                            {selectedLog.stack && (
                                <div>
                                    <Typography variant="small" className="font-semibold mb-1">Stack Trace:</Typography>
                                    <div className="bg-red-50 p-3 rounded font-mono text-sm whitespace-pre-wrap overflow-x-auto">
                                        {selectedLog.stack}
                                    </div>
                                </div>
                            )}

                            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                                <div>
                                    <Typography variant="small" className="font-semibold mb-1">Additional Data:</Typography>
                                    <div className="bg-blue-50 p-3 rounded font-mono text-sm">
                                        <pre>{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button variant="outlined" onClick={() => setShowLogDetail(false)}>
                        Close
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default LogBrowser;
